import React from 'react';
import flatten from 'lodash/flatten';
import { inject, observer } from 'mobx-react';
import { makeObservable, observable } from 'mobx';

import { EntryTypeFieldType } from 'chaika-enums';

import { Entry, SoapEntryTypeNames } from '@patient/store/entry/Entry';
import { EntryTypeList } from '@template/store/entryType/EntryTypeList';

import { EntryVersion, EntryVersionProps } from './EntryVersion';
import { EntryPage as EntryPageStore } from '@patient/ui/shared/store';

interface Props extends EntryVersionProps {}

interface StoreProps {
    entry: Entry;
    entryTypeList: EntryTypeList;
    entryPage: EntryPageStore;
}

export const skippedEntryFieldsTypes: string[] = [
    EntryTypeFieldType.RenderedService,
    EntryTypeFieldType.Referrals,
    EntryTypeFieldType.MedicalCases,
    EntryTypeFieldType.EntryEpisodes,
    EntryTypeFieldType.EpisdodesRecommendations,
    EntryTypeFieldType.EpisdodesReferrals,
    EntryTypeFieldType.EpisdodesDrugs,
    EntryTypeFieldType.EpisdodesDiagnosis,
    EntryTypeFieldType.EpisdodesTasks,
    EntryTypeFieldType.EpisdodesPlanDescription,
    EntryTypeFieldType.UsedMedications,
];

@observer
class Component extends React.Component<Props & StoreProps> {
    private rightContentMenu: React.RefObject<HTMLDivElement | null> = React.createRef();

    @observable
    private menuHeight = 0;

    constructor(props: Props & StoreProps) {
        super(props);
        makeObservable(this);
    }

    public componentDidMount(): void {
        const {
            entryPage: { rightContentMenu },
        } = this.props;

        const items = this.getItemParams();

        rightContentMenu.setItems(items);
        rightContentMenu.selectedItemIndex = 0;
    }

    public componentWillUpdate(nextProps: Readonly<Props>, nextState: Readonly<{}>, nextContext: any): void {
        setTimeout(() => {
            this.menuHeight = this.rightContentMenu.current ? this.rightContentMenu.current.scrollHeight : 0;
        }, 100);
    }

    public render(): React.ReactNode {
        const {
            entryPage: { rightContentMenu },
        } = this.props;

        const selectedSection = rightContentMenu.selectedItem;

        return React.createElement(EntryVersion, {
            menuHeight: this.menuHeight,
            menuRef: this.rightContentMenu,
            isAllEntrySelected: !!(selectedSection && selectedSection.value === 'all'),
        });
    }

    private getItemParams() {
        const { entry } = this.props;

        const sections = entry.entryType.sections;
        const subjectiveSections = entry.soapEntryType.get(SoapEntryTypeNames.SubjectiveEntryType)?.sections ?? [];
        const assessmentSections = entry.soapEntryType.get(SoapEntryTypeNames.AssessmentEntryType)?.sections ?? [];
        const paymentSections = entry.soapEntryType.get(SoapEntryTypeNames.PaymentEntryType)?.sections ?? [];

        return [...subjectiveSections, ...sections, ...assessmentSections, ...paymentSections]
            .map(section => {
                const blocks = section.blocks;

                const fields = blocks.map(block =>
                    block.fields
                        .map(field => ({
                            title: (field.title && field.title !== '' ? field.title : block.title) || section.title,
                            value: field.name,
                            type: field.type,
                        }))
                        .filter(
                            ({ value }) => entry.fieldNames.includes(value) && !skippedEntryFieldsTypes.includes(value),
                        ),
                );

                return {
                    title: section.title !== '' ? section.title : blocks[0].title,
                    subItems: flatten(fields),
                };
            })
            .filter(item => item.subItems.length > 0 && item.title !== '');
    }
}

export const EntryVersionContainer = inject<Props, StoreProps>(
    Entry.Name,
    EntryTypeList.Name,
    EntryPageStore.Name,
)(Component);
